'use client';

import { isHeycarConnectDealer } from '@core/Utils/Vehicle/IsHeycarConnect';
import Button from '@gds/Button/Button';
import { Typography } from '@gds/Typography/Typography';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';

import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { generateCheckoutStartTrackingData } from '@tracking/LeadCTA.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { calculationAtom } from '../../Atoms/Calculation.atom';
import { hasClickedApplyFinanceAtom } from '../../Atoms/HasClickedApplyFinance.atom';
import { isLeasingFinancing } from '../../Utils/isFinanceCaptive';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { currentCheckoutVehicleAtom } from '@core/Atoms/Vehicle/CurrentCheckoutVehicle.atom';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import { Locale } from '@core/Entities/Locale/Locale.entity';

import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { usePathname } from 'next/navigation';

import { CalculatorMessages } from './Calculator.messages';
import { isNb, isPfs, isRciDe, isVwfs, mapperFinanceProducts } from './Helpers/FinanceMap';
import { SummaryDE } from './Summary/SummaryDE';
import { SummaryFR } from './Summary/SummaryFR';
import { SummaryUK } from './Summary/SummaryUK';

import { useNavigateToCheckout } from '../../Hooks/useNavigateToCheckout';
import styles from './Calculator.module.css';

export type CalculatorProps = {
  locale: Locale;
  strings: CalculatorMessages;
};

export const CalculatorSummary = ({ strings, locale }: CalculatorProps) => {
  const calculation = useAtomValue(calculationAtom)!;
  const flags = useAtomValue(flagsAtom);
  const pathname = usePathname();

  const setClickedApplyFinance = useSetAtom(hasClickedApplyFinanceAtom);
  const canShowUniversalCalc = flags.showUniversalCalculator;
  const showFinanceCalcCta = canShowUniversalCalc && !pathname.includes('/finance-deal');

  const { handleNavigateToCheckoutClick } = useNavigateToCheckout({ locale });
  const vehicle = useAtomValue(currentVehicleAtom)!;
  const isEcomNotVsp = vehicle.ecom?.isEcom && vehicle?.spec.d2cModel !== 'vsp';
  const isVsp = vehicle.spec.d2cModel === 'vsp';
  const financeRate = vehicle.finance?.rate;
  const showLenderNote = financeRate !== undefined && locale === 'uk';
  const userId = useAtomValue(userIdAtom);
  const formState = useAtomValue(formStateAtom);
  const listingId = useAtomValue(currentVehicleIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const setCurrentvehicle = useSetAtom(currentCheckoutVehicleAtom);
  const currentProductId = calculation?.selectedProduct;
  const product = calculation?.products?.[currentProductId as any];
  const isUkCheckoutVehicle = locale === 'uk' && (isEcomNotVsp || isVsp);
  const hasHeyCarConnect = flags.heycarConnect && isHeycarConnectDealer(vehicle);

  const captiveId = calculation?.captiveId;
  const isCurrentVwfs = isVwfs(captiveId);
  const hasLeasingFinancing = isLeasingFinancing(calculation);
  const hasVwfsLeasing = hasLeasingFinancing && isCurrentVwfs;
  const isCurrentPorsche = isPfs(captiveId);
  const isCurrentMobilize = isRciDe(captiveId) || isNb(captiveId);
  const selectedProduct = calculation?.selectedProduct;
  const handleApplyClick = () => {
    setCurrentvehicle(vehicle);
    const {
      checkoutStart: { event, context },
    } = generateCheckoutStartTrackingData({
      searchParams,
      userId,
      formState,
      listingId,
      vehicle,
      ctaLocation: 'finance_calculator',
    });
    trackCustomEvent({ event, context });
    setClickedApplyFinance(true);
  };

  const SummaryConfig = {
    uk: SummaryUK,
    fr: SummaryFR,
    de: SummaryDE,
  };
  const SummaryComponent = SummaryConfig[locale];

  const getSummaryTitle = () => {
    if (hasVwfsLeasing) return mapperFinanceProducts['vwfsPrivateLeasing']?.summaryTitle[locale];
    if (isCurrentPorsche && selectedProduct === 'privateLeasing')
      return strings.porscheSummaryTitle;
    if (isCurrentMobilize) return strings.mobilizeSummaryTitle;
    return mapperFinanceProducts[currentProductId]?.summaryTitle[locale];
  };

  return (
    <div
      className={`${
        canShowUniversalCalc ? styles.universalSummaryContainer : styles.summaryContainer
      }`}
    >
      <div className={styles.summarytitle}>
        <Typography variant="h5">{getSummaryTitle()}</Typography>

        {locale === 'uk' && (
          <Typography variant="body4" Component="p">
            {mapperFinanceProducts[currentProductId]?.description[locale]}
          </Typography>
        )}
      </div>

      {product && (
        <SummaryComponent
          calculation={calculation}
          product={product}
          locale={locale}
          summaryStrings={strings}
        />
      )}

      <footer
        className={`${canShowUniversalCalc ? styles.universalCtasContainer : styles.ctasContainer}`}
      >
        {showLenderNote && (
          <Typography Component="p" variant="body2" className={styles.lenderNote}>
            {strings.lenderNote}
          </Typography>
        )}

        {isUkCheckoutVehicle ? (
          <Button
            Component={Link}
            dataTestId="apply_for_finance"
            variant="outlined"
            fullWidth
            href={`/${locale}/checkout/${vehicle.heycarId}/about-you`}
            onClick={handleApplyClick}
          >
            {strings.startJourney}
          </Button>
        ) : (
          locale === 'uk' && (
            <Button
              variant="outlined"
              fullWidth
              type="submit"
              onClick={handleNavigateToCheckoutClick}
            >
              {strings.startJourney}
            </Button>
          )
        )}
        {showFinanceCalcCta && (
          <Button
            className={styles.universalButton}
            variant="outlined"
            fullWidth={false}
            type="submit"
            onClick={handleNavigateToCheckoutClick}
          >
            {hasHeyCarConnect ? strings.startHeycarConnectJourney : strings.startUniversalJourney}{' '}
          </Button>
        )}
      </footer>
    </div>
  );
};

CalculatorSummary.displayName = 'CalculatorSummary';
